import { InputAdornment, TextField } from "@mui/material";
import * as React from "react";

import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import type { FileRecord } from "@/shared/domain/files";
import { Search } from "@/shared/foundation/icons";
import { useDebouncedCallback } from "@/shared/hooks";

import { RobotoDivider } from "../../RobotoDivider";
import type { ActiveSource } from "../useFilePicker";

import { FileList } from "./FileList";
import { FileListError, FileListLoading } from "./FileListFallbacks";
import styles from "./FileView.module.css";

interface FileViewProps {
  activeSource: ActiveSource;
  onFileToggle: (file: FileRecord) => void;
  isFileSelected: (fileId: string) => boolean;
  isFileAlreadyAdded: (fileId: string) => boolean;
  includePatterns?: string[];
}

/**
 * Displays the file list for the active source.
 */
export function FileView(props: FileViewProps) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [inputValue, setInputValue] = React.useState("");

  const handleSearchChange = useDebouncedCallback((value: string) => {
    setSearchTerm(value);
  }, 300);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    handleSearchChange(value);
  };

  React.useEffect(
    function clearSearchOnSourceChange() {
      setSearchTerm("");
      setInputValue("");
    },
    // Clears search term when active source changes
    [props.activeSource],
  );

  return (
    <div className={styles.container}>
      <SearchFilesInput
        value={inputValue}
        onSearchChange={handleInputChange}
        disabled={props.activeSource === null}
      />
      <RobotoDivider />
      <ErrorBoundary fallback={({ error }) => <FileListError error={error} />}>
        <React.Suspense fallback={<FileListLoading />}>
          <FileList
            activeSource={props.activeSource}
            onFileToggle={props.onFileToggle}
            isFileSelected={props.isFileSelected}
            isFileAlreadyAdded={props.isFileAlreadyAdded}
            includePatterns={props.includePatterns}
            searchTerm={searchTerm}
          />
        </React.Suspense>
      </ErrorBoundary>
    </div>
  );
}

interface SearchFilesInputProps {
  value: string;
  onSearchChange: (value: string) => void;
  disabled: boolean;
}

function SearchFilesInput({
  value,
  onSearchChange,
  disabled,
}: SearchFilesInputProps) {
  return (
    <div className={styles.searchContainer}>
      <TextField
        disabled={disabled}
        fullWidth
        size="small"
        placeholder="Search files..."
        value={value}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search className={styles.searchIcon} />
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
}
